/**
 * Ship input contract.
 *
 * Anything that produces ship inputs (a keyboard+mouse controller, a touch
 * controller, a future gamepad controller, a recorded-input bot in tests)
 * implements `ShipInputSource`. The simulator and the network client only
 * ever see `ShipInput`, so swapping controllers requires no downstream
 * changes. This is the seam the Phase 1.5 mobile rollout depends on, and
 * it lives in `shared` so both the client and the server-side sim use the
 * same definition.
 *
 * Routing of user actions through the codebase:
 *
 *   device events (keyboard / mouse / pointer)
 *     -> client/src/input/<Controller>.ts        (translates raw events into ShipInput)
 *        -> client/src/game/LocalSim.ts          (consumes ShipInput per sim tick)
 *        -> client/src/net/NetClient.ts          (serializes the same ShipInput on the wire)
 *
 * All axes are clamped to their documented ranges by the controller; the
 * simulator may assume valid input.
 */

/** Per-tick input snapshot consumed by the simulator and the net client. */
export interface ShipInput {
	/** Throttle target in [0, 1]. Sets the desired thrust intensity, but a
	 *  separate `thrust` flag still gates whether the engine is firing. */
	throttle: number;
	/** Pitch axis in [-1, 1]; positive pitches the nose up. */
	pitch: number;
	/** Yaw axis in [-1, 1]; positive yaws to the right. */
	yaw: number;
	/** Roll axis in [-1, 1]; positive rolls clockwise from the pilot's view. */
	roll: number;
	/** True while the main thruster is held. Independent of `throttle` so a
	 *  controller can present "throttle target + hold-to-burn" semantics. */
	thrust: boolean;
	/** Edge-triggered: true for exactly one sample after the player pings. */
	ping: boolean;
	/** Edge-triggered: true for exactly one sample after the player toggles
	 *  the map camera. */
	toggleMap: boolean;
}

/** A producer of `ShipInput` snapshots. */
export interface ShipInputSource {
	/** Sample current input. Called once per simulation tick. The returned
	 *  object may be reused by the controller across calls; callers must
	 *  copy fields they need to retain across ticks. */
	sample(): ShipInput;
	/** Optional teardown for controllers that own DOM listeners or timers. */
	dispose?(): void;
}

/** Returns a fresh, fully-zeroed `ShipInput`. Useful as a default state and
 *  for tests. */
export function zeroShipInput(): ShipInput {
	return {
		throttle: 0,
		pitch: 0,
		yaw: 0,
		roll: 0,
		thrust: false,
		ping: false,
		toggleMap: false,
	};
}

/** Clamps a single axis value to [-1, 1]. Exported so controllers and the
 *  simulator agree on rounding behavior at the edges. */
export function clampAxis(v: number): number {
	if (v < -1) return -1;
	if (v > 1) return 1;
	return v;
}

/** Clamps a unit value to [0, 1]. */
export function clampUnit(v: number): number {
	if (v < 0) return 0;
	if (v > 1) return 1;
	return v;
}
